/** DashboardToolCard - one large tool card (icon, name, description, footer). */
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toolUsageRelativeTime } from './toolUsageRelativeTime'

export interface DashboardToolCardProps {
  name: string
  path: string
  Icon: React.ComponentType<{ className?: string }>
  lastUsedAt: string | null
  count: number
  description?: string
}

export function DashboardToolCard({
  name,
  path,
  Icon,
  lastUsedAt,
  count,
  description,
}: DashboardToolCardProps) {
  const { t } = useTranslation()
  const relative = toolUsageRelativeTime(lastUsedAt, t)

  return (
    <Link
      to={path}
      className="group flex h-full flex-col gap-2 rounded-xl bg-white/80 p-4 dark:bg-neutral-900/80 border border-neutral-200/60 dark:border-neutral-800/60 hover:border-primary-400 dark:hover:border-primary-500 hover:-translate-y-0.5 transition-all"
    >
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg bg-primary-100/60 dark:bg-primary-950/60 border border-primary-200/20 dark:border-primary-900/20 text-primary-600 dark:text-primary-400">
          <Icon className="h-4.5 w-4.5" />
        </div>
        <span
          data-testid="dashboard-tool-name"
          className="text-sm font-semibold text-neutral-800 dark:text-neutral-200 truncate"
        >
          {name}
        </span>
      </div>
      {description && (
        <p className="text-xs text-neutral-500 dark:text-neutral-500 line-clamp-1">
          {description}
        </p>
      )}
      <div className="mt-auto flex items-center justify-between gap-2 text-xs text-neutral-400 dark:text-neutral-500">
        <span className="truncate">{relative ?? ''}</span>
        {count > 0 && (
          <span className="flex-shrink-0 font-medium text-primary-600 dark:text-primary-400">
            {t('dashboardTools.badge', { count })}
          </span>
        )}
      </div>
    </Link>
  )
}
